/**
 * TranslateSubtitleModal - Modal for initiating subtitle translation
 *
 * Allows admin to:
 * - Select source language (pre-populated from subtitle)
 * - Select target language
 * - Choose formality level (for supported languages)
 * - Select content type preset for translation instructions
 */

/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */

import { authenticatedFetch } from "@lumi/data";
import { AlertTriangle, Languages, Loader2, X } from "lucide-react";
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import { Combobox } from "../ui/combobox";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import type { ExistingSubtitle } from "./SubtitleRow";

/** Translation preview item from API */
export interface TranslationPreviewItem {
  index: number;
  startTime: string;
  endTime: string;
  originalText: string;
  translatedText: string;
}

/** Translation result from API */
export interface TranslationResult {
  translatedVtt: string;
  sourceLanguage: string;
  targetLanguage: string;
  cueCount: number;
  characterCount: number;
  preview: TranslationPreviewItem[];
  estimatedCost: {
    characters: number;
    usdEstimate: number;
  };
  tempStorageKey: string;
  expiresAt: string;
}

/** Supported language from API */
interface SupportedLanguage {
  language: string;
  name: string;
  supportsFormality?: boolean;
  supportsCustomInstructions?: boolean;
}

/** Content type presets with translation instructions */
const CONTENT_TYPE_PRESETS = {
  documentary: {
    label: "Documentary",
    description: "Formal, precise, technical",
    instructions: [
      "Use precise technical terminology",
      "Maintain formal academic tone",
      "Preserve proper nouns and place names",
    ],
    formality: "more" as const,
  },
  entertainment: {
    label: "Entertainment",
    description: "Natural, expressive, spoken",
    instructions: [
      "Keep translations concise for subtitle readability",
      "Preserve emotional tone of dialogue",
      "Use natural spoken language, not literary style",
    ],
    formality: "default" as const,
  },
  kids: {
    label: "Kids Content",
    description: "Simple, clear, age-appropriate",
    instructions: [
      "Use simple vocabulary appropriate for children",
      "Avoid idioms that don't translate well",
      "Keep sentences short and clear",
    ],
    formality: "less" as const,
  },
  custom: {
    label: "Custom",
    description: "Default settings",
    instructions: [],
    formality: "default" as const,
  },
};

type ContentType = keyof typeof CONTENT_TYPE_PRESETS;
type Formality = "default" | "more" | "less" | "prefer_more" | "prefer_less";

interface TranslateSubtitleModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly subtitle: ExistingSubtitle | null;
  readonly onTranslationComplete: (result: TranslationResult) => void;
}

export function TranslateSubtitleModal({
  open,
  onOpenChange,
  subtitle,
  onTranslationComplete,
}: TranslateSubtitleModalProps): ReactElement {
  const { accessToken, refreshAuth } = useAuth();
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  // Form state
  const [sourceLanguage, setSourceLanguage] = useState("");
  const [targetLanguage, setTargetLanguage] = useState("");
  const [contentType, setContentType] = useState<ContentType>("entertainment");
  const [formality, setFormality] = useState<Formality>("default");

  // API state
  const [sourceLanguages, setSourceLanguages] = useState<SupportedLanguage[]>([]);
  const [targetLanguages, setTargetLanguages] = useState<SupportedLanguage[]>([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when subtitle changes
  useEffect(() => {
    if (subtitle) {
      // Pre-populate source language from subtitle
      setSourceLanguage(subtitle.lang.toUpperCase());
      setTargetLanguage("");
      setContentType("entertainment");
      setError(null);
    }
  }, [subtitle]);

  // Update formality when content type changes
  useEffect(() => {
    const preset = CONTENT_TYPE_PRESETS[contentType];
    setFormality(preset.formality);
  }, [contentType]);

  // Fetch supported languages on open
  useEffect(() => {
    if (open && accessToken && sourceLanguages.length === 0) {
      void fetchLanguages();
    }
  }, [open, accessToken]);

  const fetchLanguages = async (): Promise<void> => {
    setLoadingLanguages(true);
    setError(null);

    try {
      const response = await authenticatedFetch<{
        success: boolean;
        data: {
          sourceLanguages: SupportedLanguage[];
          targetLanguages: SupportedLanguage[];
        };
      }>(
        `${apiBaseUrl}/v1/admin/translation/languages`,
        { method: "GET" },
        accessToken!,
        createRefreshCallback(refreshAuth),
      );

      if (response.success) {
        setSourceLanguages(response.data.sourceLanguages);
        setTargetLanguages(response.data.targetLanguages);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load languages";
      setError(message);
    } finally {
      setLoadingLanguages(false);
    }
  };

  const handleTranslate = async (): Promise<void> => {
    if (!subtitle || !sourceLanguage || !targetLanguage || !accessToken) return;

    setTranslating(true);
    setError(null);

    try {
      const preset = CONTENT_TYPE_PRESETS[contentType];
      const targetLang = targetLanguages.find((l) => l.language.toUpperCase() === targetLanguage.toUpperCase());

      // Build request body
      const body: Record<string, unknown> = {
        sourceLanguage,
        targetLanguage,
      };

      // Add formality if target language supports it
      if (targetLang?.supportsFormality && formality !== "default") {
        body.formality = formality;
      }

      // Add custom instructions if target language supports it and we have a preset
      if (targetLang?.supportsCustomInstructions && preset.instructions.length > 0) {
        body.customInstructions = preset.instructions;
      }

      const response = await authenticatedFetch<{
        success: boolean;
        data: TranslationResult;
        error?: string;
        message?: string;
      }>(
        `${apiBaseUrl}/v1/admin/subtitles/${subtitle.id}/translate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      if (response.success) {
        onTranslationComplete(response.data);
        onOpenChange(false);
      } else {
        setError(response.message || response.error || "Translation failed");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Translation failed";
      setError(message);
    } finally {
      setTranslating(false);
    }
  };

  const selectedTargetLang = targetLanguages.find((l) => l.language.toUpperCase() === targetLanguage.toUpperCase());
  const showFormality = selectedTargetLang?.supportsFormality;

  // Convert languages to combobox options
  const sourceLanguageOptions = useMemo(
    () => sourceLanguages.map((lang) => ({ value: lang.language.toUpperCase(), label: lang.name })),
    [sourceLanguages],
  );

  const targetLanguageOptions = useMemo(
    () =>
      targetLanguages
        .filter((l) => l.language.toUpperCase() !== sourceLanguage.toUpperCase())
        .map((lang) => ({
          value: lang.language.toUpperCase(),
          label: lang.supportsFormality ? `${lang.name} ✦` : lang.name,
        })),
    [targetLanguages, sourceLanguage],
  );

  // Animation state
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setIsClosing(false);
    }
  }, [open]);

  if (!open && !isClosing) return <></>;

  const handleClose = () => {
    if (translating) return;
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onOpenChange(false);
    }, 200);
  };

  const isVisible = open && !isClosing;

  return createPortal(
    <div
      className={`fixed inset-0 z-[60] flex items-center justify-center ${isVisible ? "animate-in fade-in duration-200" : "animate-out fade-out duration-200"}`}
    >
      {/* Backdrop overlay */}
      <button
        type="button"
        className={`absolute inset-0 bg-black/50 cursor-default ${isVisible ? "animate-in fade-in duration-200" : "animate-out fade-out duration-200"}`}
        onClick={handleClose}
        aria-label="Close modal"
        disabled={translating}
      />

      {/* Modal content */}
      <div
        className={`relative bg-neutral-800/20 backdrop-blur-lg border border-neutral-800 rounded-lg shadow-xl w-full max-w-2xl flex flex-col ${isVisible ? "animate-in zoom-in-95 fade-in duration-200" : "animate-out zoom-out-95 fade-out duration-200"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-800">
          <div>
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">Translate Subtitle</h2>
            <p className="text-sm text-neutral-400 mt-1">Translate to another language using DeepL</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={translating}
            className="text-neutral-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 space-y-5 overflow-visible">
          {/* Source Language */}
          <div className="space-y-2">
            <Label className="text-sm text-neutral-300">Source Language</Label>
            <Combobox
              value={sourceLanguage || null}
              onValueChange={(v) => setSourceLanguage(v || "")}
              options={sourceLanguageOptions}
              placeholder={loadingLanguages ? "Loading..." : "Select source language"}
              searchPlaceholder="Search languages..."
              emptyMessage="No languages found"
              disabled={loadingLanguages}
              triggerClassName="h-10 px-3 bg-neutral-900/50 border border-neutral-700 hover:border-neutral-600"
              textClassName="text-sm"
              dropdownClassName="bg-neutral-900 border-neutral-700"
            />
          </div>

          {/* Target Language */}
          <div className="space-y-2">
            <Label className="text-sm text-neutral-300">Target Language</Label>
            <Combobox
              value={targetLanguage || null}
              onValueChange={(v) => setTargetLanguage(v || "")}
              options={targetLanguageOptions}
              placeholder={loadingLanguages ? "Loading..." : "Select target language"}
              searchPlaceholder="Search languages..."
              emptyMessage="No languages found"
              disabled={loadingLanguages}
              triggerClassName="h-10 px-3 bg-neutral-900/50 border border-neutral-700 hover:border-neutral-600"
              textClassName="text-sm"
              dropdownClassName="bg-neutral-900 border-neutral-700"
            />
            {targetLanguageOptions.length > 0 && (
              <p className="text-xs text-neutral-500">✦ indicates formality support</p>
            )}
          </div>

          {/* Content Type */}
          <div className="space-y-2">
            <Label className="text-sm text-neutral-300">Content Type</Label>
            <Select value={contentType} onValueChange={(v) => setContentType(v as ContentType)}>
              <SelectTrigger className="h-10 w-full px-3 bg-neutral-900/50 border border-neutral-700 hover:border-neutral-600 rounded-md text-sm text-white shadow-sm [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:opacity-50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[70] bg-neutral-900 border-neutral-700">
                {Object.entries(CONTENT_TYPE_PRESETS).map(([key, preset]) => (
                  <SelectItem key={key} value={key} className="text-sm">
                    <span>{preset.label}</span>
                    <span className="text-xs text-neutral-500 ml-2">{preset.description}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Formality (only for supported languages) */}
          {showFormality && (
            <div className="space-y-2">
              <Label className="text-sm text-neutral-300">Tone / Formality</Label>
              <Select value={formality} onValueChange={(v) => setFormality(v as Formality)}>
                <SelectTrigger className="h-10 w-full px-3 bg-neutral-900/50 border border-neutral-700 hover:border-neutral-600 rounded-md text-sm text-white shadow-sm [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:opacity-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[70] bg-neutral-900 border-neutral-700">
                  <SelectItem value="default" className="text-sm">
                    <span>Default</span>
                    <span className="text-xs text-neutral-500 ml-2">Standard formality</span>
                  </SelectItem>
                  <SelectItem value="more" className="text-sm">
                    <span>More Formal</span>
                    <span className="text-xs text-neutral-500 ml-2">Documentary, educational</span>
                  </SelectItem>
                  <SelectItem value="less" className="text-sm">
                    <span>Less Formal</span>
                    <span className="text-xs text-neutral-500 ml-2">Youth content, casual</span>
                  </SelectItem>
                  <SelectItem value="prefer_more" className="text-sm">
                    <span>Prefer Formal</span>
                    <span className="text-xs text-neutral-500 ml-2">Formal if available</span>
                  </SelectItem>
                  <SelectItem value="prefer_less" className="text-sm">
                    <span>Prefer Informal</span>
                    <span className="text-xs text-neutral-500 ml-2">Informal if available</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-900/20 border border-red-700/50 rounded text-sm text-red-400">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-neutral-800">
          <button
            type="button"
            onClick={handleClose}
            disabled={translating}
            className="px-4 py-2 text-sm text-neutral-300 hover:text-white border border-neutral-700 hover:border-neutral-600 rounded transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleTranslate()}
            disabled={!sourceLanguage || !targetLanguage || translating || loadingLanguages}
            className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-500 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {translating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Translating...
              </>
            ) : (
              <>
                <Languages className="h-4 w-4" />
                Translate
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
